import { FC, useMemo } from "react";

import { QuestionResults, Section } from "@/types/resources";
import AwarenessReport, { AwarenessReportData } from "./AwarenessReport";
import { itemsFromQuestion } from "./_itemsFromQuestion";
import { countUniqueRespondents } from "./_respondentCount";

interface AwarenessSectionResultsProps {
  data: QuestionResults[];
  section: Section;
}

/* ── Cooker ─────────────────────────────────────────────── */

/**
 * Awareness sections are templated as two questions:
 * (1) an open-ended unaided "what brands come to mind" — we cannot
 *     reliably tally this client-side, so we approximate it with
 *     the count of *non-empty* free-text answers per brand position
 *     when the section uses a multiple_choice unaided question
 *     instead, or skip if it's open_ended.
 * (2) an aided multi-choice "have you heard of these brands".
 *
 * The aided recognition question is a `multiple_choice` whose options
 * are the brand list; the unaided question is either an open-ended
 * volunteer prompt or (legacy) a second `multiple_choice` over the same
 * options. The brand list comes from the aided question's `options`, so
 * the order matches what was authored.
 */
function cookAwarenessData(
  data: QuestionResults[] | undefined
): AwarenessReportData {
  /* Order convention from the templated awareness section: when two
     multiple_choice questions exist the first is unaided (top-of-mind)
     and the second is aided (recognition); otherwise the single one is
     aided. Brands are the aided question's options. */
  const choiceQuestions = (data ?? []).filter(
    (r) => r?.question?.type === "multiple_choice"
  );
  const aidedQuestion =
    choiceQuestions.length >= 2 ? choiceQuestions[1] : choiceQuestions[0];
  const brands = itemsFromQuestion(aidedQuestion?.question).map((item) => ({
    id: item.id,
    name: item.name,
  }));

  const aidedCounts = brands.map(() => 0);
  const unaidedCounts = brands.map(() => 0);

  const tally = (q: QuestionResults | undefined, bucket: number[]) => {
    if (!q) return;
    (q.groups ?? []).forEach((g) => {
      (g.answers ?? []).forEach((a) => {
        if (a.answer.type !== "multiple_choice") return;
        (a.answer.choices ?? []).forEach((c) => {
          if (typeof c === "number" && c >= 0 && c < bucket.length) {
            bucket[c] += 1;
          }
        });
      });
    });
  };

  if (choiceQuestions.length >= 2) {
    tally(choiceQuestions[0], unaidedCounts);
    tally(choiceQuestions[1], aidedCounts);
  } else if (choiceQuestions.length === 1) {
    tally(choiceQuestions[0], aidedCounts);
  }

  /* Section-wide unique respondents (deduped on `persona.id`) — used
     as the denominator for both unaided and aided share calculations
     and shown in the chart title chip. Counting answers per question
     would over- or under-state N if some personas only answered one
     of the two questions. */
  const sampleSize = countUniqueRespondents(data);

  return { aidedCounts, brands, sampleSize, unaidedCounts };
}

/* ── Component ──────────────────────────────────────────── */

const AwarenessSectionResults: FC<AwarenessSectionResultsProps> = ({
  data,
}) => {
  const reportData: AwarenessReportData = useMemo(
    () => cookAwarenessData(data),
    [data]
  );
  return <AwarenessReport data={reportData} />;
};

export default AwarenessSectionResults;
